import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { CaropsBox } from "@/components/CaropsBox";
import { StatePill } from "@/components/StatePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowRight, Check, Loader2, SearchX, Lock, Clock, ShieldCheck, DoorOpen,
  Info, FileCheck2, ChevronDown, Home,
} from "lucide-react";
import { BookingAgent } from "@/components/rooms/BookingAgent";

const STEPS = ["Your details", "Induction", "Activation", "Services"];

export default function Workspace() {
  const { session } = useSession();
  const token = session?.token;
  const [config, setConfig] = useState(null);
  const [inv, setInv] = useState(null);
  const [ws, setWs] = useState(null);
  const [requests, setRequests] = useState([]);
  const [state, setState] = useState("loading");

  const loadWs = (id) => get(`/core/workspace/${id}`).then(setWs);
  const loadReq = (id) => get(`/core/requests?workspace_id=${id}`).then((d) => setRequests(d.requests));

  useEffect(() => {
    if (!token) { setState("nosession"); return; }
    get("/core/config").then(setConfig);
    get(`/core/invite/${token}`).then(async (d) => {
      setInv(d);
      let wsId = d.progress?.workspace_id;
      if (!wsId && !["revoked", "expired"].includes(d.status)) {
        const w = await post(`/core/invite/${token}/redeem`, {});
        wsId = w.id; setWs(w);
      }
      if (wsId) { await loadWs(wsId); loadReq(wsId); }
      setState("ok");
    }).catch(() => setState("notfound"));
  }, [token]);

  const phase = useMemo(() => {
    if (!ws) return 1;
    if (ws.activation_state === "active") return 4;
    if (ws.activation_state === "suspended") return 3;
    if (!ws.profile_complete) return 1;
    if (!ws.induction.every((i) => i.done)) return 2;
    return 3;
  }, [ws]);

  if (state === "nosession") return (
    <Mid><div className="max-w-md text-center">
      <Home className="mx-auto h-8 w-8 text-mill-muted" />
      <p className="mt-3 font-semibold text-mill-ink">No Mill Card open yet</p>
      <p className="mt-1 text-sm text-mill-muted">Open the personal invitation link The Mill sent you to begin, then your Mill Card will reopen here.</p>
      <Link to="/" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-mill-moss px-4 py-2 text-sm font-semibold text-white hover:bg-mill-moss-dark" data-testid="me-home">The Mill home</Link>
    </div></Mid>
  );
  if (state === "loading") return <Mid><Loader2 className="h-5 w-5 animate-spin" /> Opening your Mill space…</Mid>;
  if (state === "notfound") return <Mid><div className="text-center"><SearchX className="mx-auto h-8 w-8 text-mill-muted" /><p className="mt-2 font-semibold text-mill-ink">Your invitation could not be found</p></div></Mid>;

  const blocked = ["revoked", "expired"].includes(inv.status);
  const stepIdx = Math.min(Math.max(phase - 1, 0), 3);

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8" data-testid="me-workspace">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-mill-moss">Your Mill Card</p>
          <h1 className="font-display text-2xl font-bold text-mill-ink">Hello {inv.display_name}</h1>
        </div>
        {ws && <StatePill state={ws.activation_state === "active" ? "role_active" : ws.activation_state === "suspended" ? "suspended" : ws.activation_state === "awaiting_review" ? "ready_for_review" : "in_progress"} />}
      </div>

      {blocked && <p className="rounded-xl bg-[#FEE2E2] px-4 py-3 text-sm text-[#991B1B]" data-testid="me-blocked">This invitation is {inv.status} and can no longer be used.</p>}

      {ws && config && !blocked && (
        <>
          <div className="mb-7 flex items-center gap-1" data-testid="me-stepper">
            {STEPS.map((s, i) => (
              <div key={s} className="flex flex-1 items-center gap-1">
                <div className="flex flex-col items-center gap-1">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i < stepIdx ? "bg-mill-moss text-white" : i === stepIdx ? "bg-katlas-blue text-white" : "bg-stone-200 text-mill-muted"}`}>{i < stepIdx ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
                  <span className="hidden text-[10px] text-mill-muted sm:block">{s}</span>
                </div>
                {i < STEPS.length - 1 && <span className={`h-0.5 flex-1 ${i < stepIdx ? "bg-mill-moss" : "bg-stone-200"}`} />}
              </div>
            ))}
          </div>

          {phase === 1 && <Intro ws={ws} />}
          {phase === 1 && <Profile config={config} ws={ws} onSaved={setWs} />}
          {phase === 2 && <Induction config={config} ws={ws} onChange={setWs} />}
          {phase === 3 && <Activation config={config} ws={ws} />}
          {phase === 4 && <ActiveServices config={config} ws={ws} requests={requests} onReq={() => loadReq(ws.id)} />}

          <HowThisWorks ws={ws} />
        </>
      )}
    </div>
  );
}

function Mid({ children }) { return <div className="mx-auto flex max-w-3xl items-center justify-center gap-2 px-5 py-24 text-mill-muted">{children}</div>; }

function Intro({ ws }) {
  return (
    <div className="mb-4 rounded-2xl border border-stone-200 bg-white p-6" data-testid="me-intro">
      <h2 className="font-display text-lg font-semibold text-mill-ink">This is your Mill Card</h2>
      <p className="mt-1 text-sm leading-relaxed text-mill-muted">
        A private place for your profile, role-relevant guidance, requests, sharing choices and confirmations.
        You're setting up for the <b className="text-mill-ink">{ws.role_label}</b> role. Complete a few details and
        the {ws.onboarding_pack.name} to be ready for Mill review.
      </p>
    </div>
  );
}

function Profile({ config, ws, onSaved }) {
  const [profile, setProfile] = useState({});
  const [consent, setConsent] = useState(() => Object.fromEntries(config.consent_choices.map((c) => [c.id, c.default])));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const w = await post(`/core/workspace/${ws.id}/profile`, { profile, consent });
    onSaved(w); toast.success("Details saved."); setBusy(false);
  };
  const shared = config.consent_choices.filter((c) => c.required || consent[c.id]);
  const priv = config.consent_choices.filter((c) => !c.required && !consent[c.id]);
  return (
    <div className="space-y-4" data-testid="me-profile">
      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <h2 className="font-display text-xl font-bold text-mill-ink">Your details & sharing choices</h2>
        <p className="mt-1 text-sm text-mill-muted">We only ask for what's needed to arrange your role and support. Optional information stays private unless you choose to share it.</p>

        <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-mill-moss">Shared with The Mill to support your role</p>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {config.profile_fields.map((f) => (
            <div key={f.id}>
              <Label className="text-xs text-mill-muted">{f.label}</Label>
              {f.type === "select"
                ? <select data-testid={`me-profile-${f.id}`} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" value={profile[f.id] || ""} onChange={(e) => setProfile({ ...profile, [f.id]: e.target.value })}><option value="">Select…</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
                : <Input data-testid={`me-profile-${f.id}`} value={profile[f.id] || ""} onChange={(e) => setProfile({ ...profile, [f.id]: e.target.value })} />}
            </div>
          ))}
        </div>

        <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-[#B45309]">Optional — private unless you choose to share</p>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {config.optional_fields.map((f) => (
            <div key={f.id}>
              <Label className="text-xs text-mill-muted">{f.label}</Label>
              <Input data-testid={`me-optional-${f.id}`} value={profile[f.id] || ""} onChange={(e) => setProfile({ ...profile, [f.id]: e.target.value })} />
            </div>
          ))}
        </div>

        <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-mill-muted">Your sharing choices</p>
        <div className="mt-2 space-y-1.5">
          {config.consent_choices.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-mill-ink">
              <Checkbox checked={c.required || !!consent[c.id]} disabled={c.required} onCheckedChange={(v) => setConsent({ ...consent, [c.id]: v })} data-testid={`me-consent-${c.id}`} />
              {c.label} {c.required && <span className="text-[10px] uppercase text-mill-muted">(required)</span>}
            </label>
          ))}
        </div>

        <div className="mt-4 rounded-xl bg-mill-alt/60 p-3 text-xs" data-testid="me-sharing-summary">
          <p className="font-semibold text-mill-ink">In plain language:</p>
          <p className="mt-1 text-mill-moss"><b>Shared with The Mill:</b> {shared.map((c) => c.label).join("; ") || "nothing"}.</p>
          <p className="mt-0.5 text-[#B45309]"><b>Kept private:</b> {priv.map((c) => c.label).join("; ") || "nothing"}.</p>
        </div>

        <Button className="mt-4 bg-mill-moss hover:bg-mill-moss-dark" onClick={submit} disabled={busy} data-testid="me-profile-submit">Save & continue <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function Induction({ config, ws, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (i) => { setBusy(true); const w = await post(`/core/workspace/${ws.id}/induction`, { item_id: i.id, done: !i.done }); onChange(w); setBusy(false); };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="me-induction">
      <h2 className="font-display text-xl font-bold text-mill-ink">Induction — {ws.onboarding_pack.name} {ws.onboarding_pack.version}</h2>
      <p className="mt-1 text-sm text-mill-muted">Work through your role-relevant guidance. Tick each item once you've read it.</p>
      <div className="mt-4 space-y-2">
        {ws.induction.map((i) => (
          <label key={i.id} data-testid={`me-induction-${i.id}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 ${i.done ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-stone-200"}`}>
            <Checkbox checked={i.done} disabled={busy} onCheckedChange={() => toggle(i)} data-testid={`me-induction-check-${i.id}`} />
            <span className={`text-sm ${i.done ? "text-mill-ink" : "text-mill-muted"}`}>{i.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Activation({ config, ws }) {
  const suspended = ws.activation_state === "suspended";
  return (
    <div className="space-y-4" data-testid="me-activation">
      <CaropsBox carops={config.carops} stage="activation" />
      <div className="rounded-3xl border border-stone-200 bg-white p-7 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#DBEAFE] text-katlas-blue"><Clock className="h-6 w-6" /></div>
        <h2 className="mt-4 font-display text-2xl font-bold text-mill-ink">{suspended ? "Your role is currently paused" : "Onboarding complete — awaiting Mill review"}</h2>
        <p className="mt-2 text-mill-muted">{suspended ? "The Mill has paused your role. Please contact the team if you have any questions." : "Activation is a decision made by the Mill team, not an automatic result of completing a form."}</p>
        {!suspended && <p className="mt-1 text-sm text-mill-muted">Once your role is active you'll be able to request a room or service.</p>}
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-mill-muted">Available once your role is active</p>
        <div className="flex items-center gap-2 rounded-xl border border-dashed border-stone-300 bg-mill-alt/40 px-4 py-3 text-sm text-mill-muted" data-testid="me-locked-service"><Lock className="h-3.5 w-3.5" /> Request a room or service</div>
      </div>
    </div>
  );
}

function ActiveServices({ config, ws, requests, onReq }) {
  return (
    <div className="space-y-4" data-testid="me-services">
      <div className="rounded-2xl border border-[#A7F3D0] bg-[#F0FDF4] p-5" data-testid="me-active-confirm">
        <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#065F46]" /><h2 className="font-display text-lg font-bold text-mill-ink">Your {ws.role_label} role is active</h2></div>
        <p className="mt-1 text-sm text-mill-muted">Activated by {ws.activated_by}. You can now use your permitted services.</p>
      </div>
      <BookingAgent workspaceId={ws.id} config={config} />
    </div>
  );
}

function HowThisWorks({ ws }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6" data-testid="me-how-this-works">
      <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-sm font-medium text-mill-muted hover:text-mill-ink" data-testid="me-how-toggle">
        How this works (assurance) <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-stone-200 bg-white p-4 text-xs leading-relaxed text-mill-muted" data-testid="me-how-panel">
          Your Mill Card is backed by a synthetic <b className="text-mill-ink">managed cloud wallet</b> ({ws.id}). In this sandbox it
          holds no real keys, addresses or personal data. Optional responses stay in your space unless you choose to share them.
          In a future pilot this becomes a properly governed managed wallet with one-time links and server-side signing.
        </div>
      )}
    </div>
  );
}
